import '@vaadin/vaadin-overlay';
import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_GENERIC_OVERLAY } from '../../../webcomponents.constants';
import { customElement } from 'lit/decorators.js';
import { GenericOverlayEvents, GenericOverlayPosition } from '../types';
import { GenericOverlayElement } from './GenericOverlay';
import { styleMap } from 'lit/directives/style-map.js';
import { DirectiveResult } from 'lit/directive.js';

/**
 * @summary Web Component used for creating overlays with any type of content and trigger. It uses the Vaadin overlay component under the hood, extended in the GenericOverlayElement class
 * https://cdn.vaadin.com/vaadin-web-components/24.4.0-alpha3/#/elements/vaadin-overlay
 * @tag gz-generic-overlay
 * @fires generic-overlay-open - A custom event dispatched when the overlay element is open. It can be used for adding custom behavior on the overlay content.
 * @fires generic-overlay-content-click - A custom event dispatched after the content of the overlay is clicked. It can be used for manually closing the overlay
 * @fires generic-overlay-close - A custom event dispatched after the overlay element is closed
 */
@customElement(WEB_COMPONENT_GZ_GENERIC_OVERLAY)
export class GzGenericOverlayWebComponent extends LitElement {
  @property({ type: Boolean, attribute: true, reflect: true })
  open = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  attachToBody = true;

  @property({ type: String, attribute: true, reflect: true })
  overlayPositionX = GenericOverlayPosition.LEFT;

  @property({ type: String, attribute: true, reflect: true })
  overlayID = '';

  @property({ type: Number, attribute: true, reflect: true })
  overlayVerticalSpacer = 4; // the vertical distance between the target and the overlay (in pixels)

  @query('slot[name="target"]')
  private targetSlot: HTMLSlotElement;

  @query('slot[id="contentSlot"]')
  private contentSlot: HTMLSlotElement;

  private target: HTMLElement;
  private cachedStyles: DirectiveResult;
  private readonly _boundOutsideClickListener: EventListener;
  private readonly _boundResizeListener: EventListener;
  private readonly _boundScrollEventListener: EventListener;

  constructor() {
    super();
    this._boundOutsideClickListener = this.outsideClickListener.bind(this);
    this._boundResizeListener = this.onResizeListener.bind(this);
    this._boundScrollEventListener = this.onScrollEventListener.bind(this);
  }

  static get styles() {
    return styles;
  }

  connectedCallback() {
    super.connectedCallback();
    this.cachedStyles = this.getGenericOverlayElementStyles();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.open = false;
  }

  openOverlay(target: HTMLElement, openViaAttribute = false): void {
    if (!target) {
      return;
    }
    if (!openViaAttribute && this.open) {
      this.closeOverlay();
      return;
    }
    this.target = target;
    this.open = true;
    this.addEventListeners();
    this.alignOverlayToTarget();
  }

  closeOverlay(): void {
    this.open = false;
    this.removeEventListeners();

    this.dispatchEvent(new CustomEvent(GenericOverlayEvents.CLOSE, { bubbles: true, composed: true }));
  }

  updated(_changedProperties: PropertyValues): void {
    if (_changedProperties.has('open')) {
      if (this.open) {
        this.openOverlayToTargetSlot();
      } else {
        this.closeOverlay();
      }
    }
  }

  private addEventListeners(): void {
    window.addEventListener('mouseup', this._boundOutsideClickListener, true);
    window.addEventListener('resize', this._boundResizeListener, true);
    window.addEventListener('scroll', this._boundScrollEventListener, true);
    window.addEventListener('wheel', this._boundScrollEventListener, true);
  }

  private removeEventListeners(): void {
    window.removeEventListener('mouseup', this._boundOutsideClickListener, true);
    window.removeEventListener('resize', this._boundResizeListener, true);
    window.removeEventListener('scroll', this._boundScrollEventListener, true);
    window.removeEventListener('wheel', this._boundScrollEventListener, true);
  }

  private onOverlayElementOpen(event: CustomEvent): void {
    event.stopImmediatePropagation();
    this.dispatchEvent(new CustomEvent(GenericOverlayEvents.OPEN, { bubbles: true, composed: true }));
    this.target.setAttribute('active', 'true');
    if (document.body.style.pointerEvents === 'none') {
      document.body.style.pointerEvents = 'auto'; // overrides default Vaadin overlay behaviour
    }
  }

  private onOpenedChanged(): void {
    if (!this.open) {
      return;
    }
    // Perform the alignment also after the 'opened' property changed on the GenericOverlayElement (vaadin component),
    // and the overlay is rendered to the screen
    this.alignOverlayToTarget();
  }

  private onOverlayElementClose(event: CustomEvent): void {
    event.stopImmediatePropagation();
    this.target?.removeAttribute('active');
    this.dispatchEvent(new CustomEvent(GenericOverlayEvents.CLOSE, { bubbles: true, composed: true }));
  }

  private onOverlayElementContentClick(): void {
    this.dispatchEvent(new CustomEvent(GenericOverlayEvents.CONTENT_CLICK, { bubbles: true, composed: true }));
  }

  private onTargetSlotChange(): void {
    // This function is called when the target slot gets set. If the overlay should already be open, it opens the overlay.
    if (this.open) {
      this.openOverlayToTargetSlot();
    }
  }

  private outsideClickListener(event: CustomEvent): void {
    const eventPath = event.composedPath();
    if (eventPath.indexOf(this.getOverlayElement() ?? new EventTarget()) > 0) {
      return;
    }
    // We need to delay closeOverlay() to be able to close the overlay when it's already opened and openOverlay() is
    // called again via the trigger button.
    // Directly using setTimeout instead of wrapping outsideClickListener() in debounce is needed because event.composedPath()
    // is designed to retrieve the event path during the active event dispatch, therefore, if we wrap the whole function
    // with debounce, we loose the event path.
    setTimeout(() => this.closeOverlay(), 100);
  }

  private onResizeListener(): void {
    this.closeOverlay();
  }

  private onScrollEventListener(): void {
    this.alignOverlayToTarget();
  }

  private openOverlayToTargetSlot(): void {
    if (!this.targetSlot || !this.targetSlot.assignedElements()[0]) {
      return;
    }
    this.openOverlay(this.targetSlot.assignedElements()[0] as HTMLElement, true);
  }

  private getOverlayElement(): GenericOverlayElement | undefined {
    return (this.shadowRoot?.getElementById(`overlay-${this.overlayID}`) ??
      document.body.querySelector(`#overlay-${this.overlayID}`)) as GenericOverlayElement;
  }

  private shouldAlignAbove(targetClientRect: DOMRect): boolean {
    const spaceBelow =
      (window.innerHeight - targetClientRect.bottom - Math.min(document.body.scrollTop, 0)) / window.innerHeight;
    return spaceBelow < 0.3;
  }

  private alignOverlayToTarget(): void {
    const overlay = this.getOverlayElement();
    if (!overlay || !this.target) {
      return;
    }
    let translateX;
    let translateY;
    const targetRect = this.target.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    const shouldAlignAbove = this.shouldAlignAbove(targetRect);
    const devicePixelRatio = window.devicePixelRatio || 1;

    if (this.overlayPositionX === GenericOverlayPosition.LEFT) {
      translateX = targetRect.left;
    } else {
      translateX = targetRect.right - overlayRect.width;
    }

    translateX = Math.round(translateX * devicePixelRatio) / devicePixelRatio;
    translateY = Math.round((targetRect.top + targetRect.height) * devicePixelRatio) / devicePixelRatio;

    if (shouldAlignAbove) {
      const verticalOffset = overlayRect.height + targetRect.height + this.overlayVerticalSpacer;
      overlay.style.top = `-${verticalOffset}px`;
    } else {
      overlay.style.top = `${this.overlayVerticalSpacer}px`;
    }

    overlay.style.transform = `translate3d(${translateX}px, ${translateY}px, 0)`;
  }

  private beforeOverlayAttachOnBody() {
    // This function appends the elements received via the main slot to the slot of generic-overlay (a vaadin component)
    // before the generic-overlay is moved from this component and attached to document.body.
    // This is needed in order not to lose the elements provided via the slot when the move is made.
    this.contentSlot.append(...this.contentSlot.assignedElements());
  }

  private getGenericOverlayElementStyles(): DirectiveResult {
    // Set the styles provided to this component directly on generic-overlay (a vaadin component),
    // because in the case where generic-overlay will be moved to document.body, the styles will still be present on it
    const computedStyle = getComputedStyle(this);
    return styleMap({
      '--overlay-width': computedStyle.getPropertyValue('--overlay-width'),
      '--overlay-height': computedStyle.getPropertyValue('--overlay-height'),
      '--overlay-max-width': computedStyle.getPropertyValue('--overlay-max-width'),
      '--overlay-max-height': computedStyle.getPropertyValue('--overlay-max-height'),
      '--overlay-min-width': computedStyle.getPropertyValue('--overlay-min-width'),
      '--overlay-min-height': computedStyle.getPropertyValue('--overlay-min-height'),
      '--overlay-border': computedStyle.getPropertyValue('--overlay-border'),
      '--overlay-border-radius': computedStyle.getPropertyValue('--overlay-border-radius'),
      '--overlay-box-shadow': computedStyle.getPropertyValue('--overlay-box-shadow'),
    });
  }

  render(): TemplateResult {
    return html`
      <slot name="target" @slotchange="${this.onTargetSlotChange}"></slot>
      <generic-overlay
        id="overlay-${this.overlayID}"
        style="${this.cachedStyles}"
        @click="${this.onOverlayElementContentClick}"
        @generic-overlay-before-attach="${this.beforeOverlayAttachOnBody}"
        @vaadin-overlay-open="${this.onOverlayElementOpen}"
        @opened-changed="${this.onOpenedChanged}"
        @vaadin-overlay-close="${this.onOverlayElementClose}"
        ?opened="${this.open}"
        ?attachToBody="${this.attachToBody}"
      >
        <slot id="contentSlot"></slot>
      </generic-overlay>
    `;
  }
}
